import React, {useState, useEffect} from "react";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import {PhotoModel} from "../types/protocols";
import {FirebaseImage, ImageSize, useFirebaseImage} from "../Helpers/ImageLoader";
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { useUsersData } from '../Helpers/UsersContext';

interface ClickableImageProps {
    photo: PhotoModel;
    buildingKey: string;
    altText?: string;
    size?: number;
    disableClick?: boolean;
    thumbnail?: boolean;
    showDate?: boolean;
    onImageClick?: (photo: PhotoModel, fullUrl: string) => void;
    highlighted?: boolean;
    showDeleteButton?: boolean;
    onDeleteClick?: (photo: PhotoModel) => void;
}

const ClickableImage: React.FC<ClickableImageProps> = ({
                                                           photo,
                                                           buildingKey,
                                                           altText = "Component Photo",
                                                           size = 125,
                                                           disableClick = false,
                                                           thumbnail = true,
                                                           showDate = size > 80 && photo.date.length !== 0,
                                                           onImageClick,
                                                           highlighted = false,
                                                           showDeleteButton = false,
                                                           onDeleteClick,
                                                       }) => {

    const imageSize = size < 200 ? ImageSize.THUMBNAIL_SMALL : size < 600 ? ImageSize.THUMBNAIL_MEDIUM : ImageSize.FULL
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const { src, isLoading, hasError } = useFirebaseImage(photo.storagePath);
    const [user] = useAuthState(auth);
    const { users } = useUsersData();

    // Check if current user is admin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || false);
        }
    }, [user, users]);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (disableClick) return;

        // Check if the click was on the delete button
        const target = e.target as Element;
        if (target.closest('.delete-button')) {
            return;
        }

        if (onImageClick) {
            onImageClick(photo, src)
            return;
        }

        const fancyboxOptions: any = {
            animated: true,
            showClass: "fancybox-fadeIn",
        };

        // Add custom buttons if delete functionality is enabled
        if (showDeleteButton && onDeleteClick) {
            fancyboxOptions.onInit = (fancybox: any) => {
                console.log('Fancybox initialized, looking for toolbar...');
                
                // Try multiple selectors to find the toolbar
                const toolbarSelectors = [
                    '.fancybox__toolbar',
                    '.fancybox-toolbar',
                    '[data-fancybox-toolbar]',
                    '.fancybox__container .fancybox__toolbar'
                ];
                
                let toolbar = null;
                for (const selector of toolbarSelectors) {
                    toolbar = fancybox.container.querySelector(selector);
                    if (toolbar) {
                        console.log('Found toolbar with selector:', selector);
                        break;
                    }
                }
                
                if (!toolbar) {
                    // Try to find it in the document
                    for (const selector of toolbarSelectors) {
                        toolbar = document.querySelector(selector);
                        if (toolbar) {
                            console.log('Found toolbar in document with selector:', selector);
                            break;
                        }
                    }
                }
                
                if (toolbar) {
                    console.log('Creating delete button...');
                    const deleteButton = document.createElement('button');
                    deleteButton.className = 'fancybox__button fancybox__button--delete';
                    deleteButton.innerHTML = '🗑️';
                    deleteButton.title = 'Ta bort bild';
                    deleteButton.style.cssText = `
                        background: rgba(255, 255, 255, 0.1);
                        border: none;
                        border-radius: 4px;
                        color: white;
                        cursor: pointer;
                        font-size: 16px;
                        height: 40px;
                        margin: 0 4px;
                        padding: 0 12px;
                        transition: background-color 0.2s;
                        z-index: 9999;
                    `;
                    deleteButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Delete button clicked for photo:', photo.key);
                        onDeleteClick(photo);
                    });
                    deleteButton.addEventListener('mouseenter', () => {
                        deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    });
                    deleteButton.addEventListener('mouseleave', () => {
                        deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    });
                    
                    // Try to insert before the close button
                    const closeButton = toolbar.querySelector('.fancybox__button--close');
                    if (closeButton) {
                        toolbar.insertBefore(deleteButton, closeButton);
                        console.log('Delete button inserted before close button');
                    } else {
                        toolbar.appendChild(deleteButton);
                        console.log('Delete button appended to toolbar');
                    }
                } else {
                    console.log('Toolbar not found, trying alternative approach...');
                    // Alternative approach: add button to the container directly
                    setTimeout(() => {
                        const container = document.querySelector('.fancybox__container');
                        if (container) {
                            const deleteButton = document.createElement('button');
                            deleteButton.className = 'fancybox__button fancybox__button--delete';
                            deleteButton.innerHTML = '🗑️';
                            deleteButton.title = 'Ta bort bild';
                            deleteButton.style.cssText = `
                                position: absolute;
                                top: 20px;
                                right: 60px;
                                background: rgba(255, 255, 255, 0.1);
                                border: none;
                                border-radius: 4px;
                                color: white;
                                cursor: pointer;
                                font-size: 16px;
                                height: 40px;
                                padding: 0 12px;
                                transition: background-color 0.2s;
                                z-index: 9999;
                            `;
                            deleteButton.addEventListener('click', (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Delete button clicked for photo:', photo.key);
                                onDeleteClick(photo);
                            });
                            deleteButton.addEventListener('mouseenter', () => {
                                deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                            });
                            deleteButton.addEventListener('mouseleave', () => {
                                deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            });
                            container.appendChild(deleteButton);
                            console.log('Delete button added to container');
                        }
                    }, 100);
                }
            };
        }

        Fancybox.show(
            [
                {
                    src: src,
                    caption: photo.date,
                }
            ],
            fancyboxOptions
        );
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (onDeleteClick) {
            onDeleteClick(photo);
        }
        setShowDeleteModal(false);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
    };



    return (
        <div
            style={{
                position: 'relative',
                width: size,
                height: size,
                borderRadius: '5px',
                overflow: 'hidden',
            }}
        >
            {highlighted &&
                <div style={{position: 'absolute', backgroundColor: '#FFFFFFAA', width: '100%', height: '100%'}}/>
            }
            <div
                data-fancybox="gallery"
                data-caption={photo.date}
                onClick={handleClick}
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                }}
            >
                <FirebaseImage
                    key={photo.key}
                    alt={altText}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: disableClick ? 'default' : 'pointer',
                    }}
                    size={imageSize}
                    storagePath={photo.storagePath}
                />
            </div>
            {showDate && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        textAlign: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: '#fff',
                        padding: '5px 0',
                        borderBottomLeftRadius: '5px',
                        borderBottomRightRadius: '5px',
                        fontSize: '14px',
                        zIndex: '99',
                        pointerEvents: 'none',
                    }}
                >
                    {photo.date.split(" ")[0]}
                </div>
            )}

            {/* Delete button - only show for admins */}
            {showDeleteButton && isAdmin && (
                <button
                    className="delete-button"
                    onClick={handleDeleteClick}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'rgba(0, 0, 0, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        zIndex: 1000,
                        transition: 'all 0.2s',
                        pointerEvents: 'auto',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(75, 75, 75, 1)';
                        e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                    }}
                >
                    🗑️
                </button>
            )}

            {/* Delete confirmation modal */}
            {showDeleteModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                    }}
                    onClick={handleCancelDelete}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '24px',
                            maxWidth: '400px',
                            margin: '20px',
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>
                            Radera bild
                        </h3>
                        <p style={{ margin: '0 0 24px 0', color: '#666' }}>
                            Är du säker på att du vill radera bilden? Detta kan inte ångras.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleCancelDelete}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    background: 'white',
                                    color: '#333',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    background: '#dc3545',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                Radera
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ClickableImage;
